import clsx from 'clsx'
import {FormikProps, getIn} from 'formik'
import {cloneDeep, find, get, isEmpty, isEqual} from 'lodash'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'

import {
  CARRIER_PACKAGE_TYPE,
  DEFAULT_COLOR,
  INVALID_COLOR,
  VALID_COLOR,
  DIMENSION_UNIT,
  OPTION_PACKAGE_TYPE,
  WEIGHT_UNIT,
} from '../../../../../../gori/constants'
import {toAbsoluteUrl} from '../../../../../../gori/helpers'
import {KTSVG} from '../../../../../../gori/components'

type Props = {
  data: any
  textLabel?: string
  labelClassName?: any
  required?: boolean
  formik: FormikProps<any>
  name: string
  placeholder?: string
  className?: any
  showAddPackage?: boolean
  disabled?: boolean
  labelPackageType?: string
  setLabelPackageType?: React.Dispatch<React.SetStateAction<string | undefined>>
}

export type ParcelItem = {
  id: number
  package_id: number | null
  package_type: string | null
  length: number
  width: number
  height: number
  weight: number
  dimension_unit: string
  weight_unit: string
  package_count?: number
  editable: boolean
  hidden: string
}

const PackageType: React.FC<Props> = ({
  data,
  textLabel,
  labelClassName,
  required,
  formik,
  name,
  placeholder = '',
  className,
  disabled = false,
  labelPackageType,
  setLabelPackageType = () => {},
}) => {
  const storeRef = useRef<any>(null)
  const intl = useIntl()
  const [visibleMenu, setVisibleMenu] = useState(false)
  const [visibleSubMenu, setVisibleSubMenu] = useState<string | undefined>(undefined)

  const firstParcel = Object.values(formik.values.parcels)?.[0] as ParcelItem
  const showInputDimension = !!data.find(
    (option) =>
      isEqual(option, OPTION_PACKAGE_TYPE.INPUT_DIMENSION) &&
      (firstParcel?.height || firstParcel?.length || firstParcel?.width)
  )

  const prevParcelsRef = useRef<ParcelItem | null>(null)
  const prevUnitRef = useRef(formik.values?.parcels?.[0]?.dimension_unit)

  let fieldProps = formik.getFieldProps(name)
  let formikErrors = getIn(formik.errors, name)
  let formikTouched = getIn(formik.touched, name)
  let isInvalid = formikTouched && formikErrors
  let isValid = formikTouched && !formikErrors && fieldProps.value

  const customStyle = {
    borderColor: isValid ? VALID_COLOR : isInvalid ? INVALID_COLOR : DEFAULT_COLOR,
    borderWidth: '1px',
    borderStyle: 'solid',
    width: '100%',
    '&:hover': {
      borderColor: isValid ? VALID_COLOR : isInvalid ? INVALID_COLOR : DEFAULT_COLOR,
    },
    boxShadow: isValid ? VALID_COLOR : isInvalid ? INVALID_COLOR : DEFAULT_COLOR,
    backgroundImage: isValid
      ? `url(${toAbsoluteUrl('/media/gori/common/valid.svg')})`
      : isInvalid
      ? `url(${toAbsoluteUrl('/media/gori/common/invalid.svg')})`
      : '',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 3.5rem center',
    backgroundSize: 'calc(0.75em + 0.75rem) calc(0.75em + 0.75rem)',
    backgroundColor: (disabled && '#eff2f5') || '#ffffff ',
    fontSize: '1.1rem',
    fontWeight: '500',
    color: Object.keys(formik.getFieldProps('parcels').value).length > 1 ? '#94A3B8' : '#5e6278',
    lineHeight: '1.5',
    padding: '3px',
  }

  const handleDropdownClick = () => {
    setVisibleMenu(!visibleMenu)
  }

  const openChoose = useCallback(() => {
    let found = false
    let parcelsCurrent = cloneDeep(Object.values(formik.values.parcels)?.[0]) as ParcelItem

    // Handle Input Dimension case first
    if (!parcelsCurrent?.package_type && !parcelsCurrent?.package_id) {
      if (showInputDimension) {
        setLabelPackageType(OPTION_PACKAGE_TYPE.INPUT_DIMENSION.label)
        setVisibleSubMenu(OPTION_PACKAGE_TYPE.INPUT_DIMENSION.label)
        return
      } else {
        setLabelPackageType(placeholder)
        return
      }
    }

    // Check in data for matching package
    data?.forEach((item) => {
      let {label, options} = item
      if (!options && label === OPTION_PACKAGE_TYPE.INPUT_DIMENSION.label) return

      if (!options) {
        options = [item]
      }

      options.forEach((subItem) => {
        if (found) return
        const package_type = get(subItem, 'value.package_type', '')
        const package_id = get(subItem, 'value.package_id')
        const length = get(subItem, 'value.length')
        const width = get(subItem, 'value.width')
        const height = get(subItem, 'value.height')
        const dimension_unit = get(subItem, 'value.dimension_unit')

        const checkEqual =
          Number(length) === Number(parcelsCurrent.length) &&
          Number(width) === Number(parcelsCurrent.width) &&
          Number(height) === Number(parcelsCurrent.height) &&
          dimension_unit === parcelsCurrent.dimension_unit

        // For custom package, check by package_id
        if (
          label === OPTION_PACKAGE_TYPE.CUSTOM_PACKAGE.label &&
          package_id === parcelsCurrent?.package_id &&
          checkEqual
        ) {
          found = true
          setVisibleSubMenu(label)
          setLabelPackageType(subItem.label)
          return
        }

        // For carrier packages, check by package_type
        const carrierLabels = CARRIER_PACKAGE_TYPE.map((item) => item.label)
        if (package_type === parcelsCurrent?.package_type && carrierLabels.includes(label)) {
          found = true
          setVisibleSubMenu(label)
          setLabelPackageType(subItem.label)
          return
        }
      })
    })
    const firstParcelKey = Object.keys(formik.values.parcels)?.[0]
    formik.setFieldValue(`parcels.${firstParcelKey}`, parcelsCurrent)
    prevParcelsRef.current = parcelsCurrent
    prevUnitRef.current = parcelsCurrent.dimension_unit

    // If no match found and not already set to Input Dimension
    if (!found && labelPackageType !== OPTION_PACKAGE_TYPE.INPUT_DIMENSION.label) {
      setLabelPackageType(
        showInputDimension ? OPTION_PACKAGE_TYPE.INPUT_DIMENSION.label : placeholder
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    formik.values?.parcels,
    labelPackageType,
    placeholder,
    setLabelPackageType,
    showInputDimension,
  ])

  useEffect(() => {
    const parcels = formik.values?.parcels || {}
    const firstParcel: any = Object.values(parcels)?.[0]
    const prevParcels = prevParcelsRef.current

    // If the parcel has changed (compared to the previous one), call openChoose again
    if (!isEqual(prevParcels, firstParcel)) {
      openChoose()
      prevParcelsRef.current = firstParcel
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values?.parcels])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (storeRef.current && !storeRef.current.contains(event.target)) {
        setVisibleMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openChoose])

  const renderMenuItem = (item, index) => {
    const {label, options} = item

    const highLightPackageCarrier = (subItem) => {
      const parcelsCurrent = Object.values(formik.values.parcels)?.[0] as ParcelItem
      if (!parcelsCurrent) return false
      const {
        value: {package_type, package_id},
      } = subItem

      const checkEqual = options.filter((item) => {
        return (
          item.value.length === Number(parcelsCurrent?.length) &&
          item.value.width === Number(parcelsCurrent?.width) &&
          item.value.height === Number(parcelsCurrent?.height) &&
          item.value.dimension_unit === parcelsCurrent?.dimension_unit
        )
      })
      const itemFound = find(checkEqual, {label: subItem.label})
      if (
        package_type === parcelsCurrent?.package_type &&
        package_id === parcelsCurrent?.package_id &&
        itemFound
      ) {
        return true
      } else {
        return false
      }
    }

    const handleClickPackage = async (item, options) => {
      const currentParcels = formik.values.parcels || {}
      const [firstParcelKey, firstParcel] = Object.entries(currentParcels)?.[0] as [
        firstParcelKey: string,
        firstParcel: ParcelItem
      ]

      if (label === OPTION_PACKAGE_TYPE.INPUT_DIMENSION.label) {
        setVisibleMenu(false)
        setVisibleSubMenu(label)

        await formik.setFieldValue('parcels', {
          [firstParcelKey]: {
            length: '',
            width: '',
            height: '',
            package_id: null,
            package_type: '',
            package_count: firstParcel.package_count || 1,
            weight: firstParcel.weight,
            dimension_unit: DIMENSION_UNIT.IN,
            weight_unit: firstParcel.weight_unit ?? WEIGHT_UNIT.OZ,
          },
        })
        setLabelPackageType(OPTION_PACKAGE_TYPE.INPUT_DIMENSION.label)
      } else {
        // Case USPS Cubic
        if (isEmpty(options) && label !== OPTION_PACKAGE_TYPE.CUSTOM_PACKAGE.label) {
          await formik.setFieldValue('parcels', {
            [firstParcelKey]: {
              ...currentParcels[firstParcelKey],
              ...item.value,
              package_count: 1,
            },
          })
          setVisibleMenu(false)
        } else {
          setVisibleSubMenu((prev) => (prev === label ? undefined : label))
        }
      }
    }

    const handleClickPackageChild = async (subItem) => {
      setVisibleMenu(false)
      const currentParcels = formik.values.parcels || {}
      const [firstParcelKey, firstParcel] = Object.entries(currentParcels)?.[0] as [
        firstParcelKey: string,
        firstParcel: ParcelItem
      ]
      setLabelPackageType(subItem.label)
      const newFirstParcel = {
        ...firstParcel,
        dimension_unit: firstParcel.dimension_unit || DIMENSION_UNIT.IN,
        weight_unit: firstParcel.weight_unit || WEIGHT_UNIT.OZ,
        package_count: firstParcel.package_count || '',
        ...subItem.value,
      }
      await formik.setFieldValue('parcels', {
        ...currentParcels,
        [firstParcelKey]: newFirstParcel,
      })
    }

    return (
      <div className={clsx('dropdown-submenu', {show: visibleSubMenu === label})} key={index}>
        <div
          className={clsx('dropdown-submenu__item item-custom', {
            show: visibleSubMenu === label,
          })}
          onClick={() => handleClickPackage(item, options)}
        >
          {label === OPTION_PACKAGE_TYPE.INPUT_DIMENSION.label ||
          label === OPTION_PACKAGE_TYPE.CUSTOM_PACKAGE.label
            ? intl.formatMessage({id: label})
            : label}
        </div>
        <div
          className={clsx('dropdown-submenu__inner', {show: visibleSubMenu === label})}
          style={{overflowY: 'auto', maxHeight: '200px'}}
        >
          {!isEmpty(options) &&
            options?.map((subItem) => {
              return (
                <div
                  key={subItem.label}
                  className={`dropdown-submenu__inner__item item-custom ${
                    highLightPackageCarrier(subItem)
                      ? 'text-white bg-primary'
                      : 'text-hover-primary bg-hover-active-primary'
                  }`}
                  onClick={() => handleClickPackageChild(subItem)}
                >
                  {subItem.label}
                </div>
              )
            })}
        </div>
      </div>
    )
  }

  return (
    <>
      {textLabel && (
        <label className={`form-label ${labelClassName} ${required ? 'required' : ''}`}>
          {textLabel}
        </label>
      )}
      <div className={clsx('dropdown', className, {'cursor-no-drop': disabled})} ref={storeRef}>
        <div
          className={clsx('dropdown__label py-3 px-3', {'pe-none': disabled})}
          onClick={handleDropdownClick}
          style={customStyle}
        >
          <div className='dropdown__label__left fw-bold'>
            <div>
              {labelPackageType === OPTION_PACKAGE_TYPE.INPUT_DIMENSION.label ||
              labelPackageType === OPTION_PACKAGE_TYPE.CUSTOM_PACKAGE.label
                ? intl.formatMessage({id: labelPackageType})
                : labelPackageType}
            </div>
          </div>
          <div
            style={{
              borderLeft: '1px solid hsl(0, 0%, 80%)',
              paddingLeft: '6px',
              boxSizing: 'border-box',
              transition: 'color 150ms',
              fill: 'hsl(0, 0%, 80%)',
            }}
            className='dropdown__label__arrow'
          >
            <KTSVG path='/media/gori/select_arrow.svg' svgClassName={''} small={false} />
          </div>
        </div>
        <div
          className={clsx('dropdown-menu dropdown__menu  mw-100', {show: visibleMenu})}
          style={{width: '100%'}}
        >
          {data.map((item, index) => renderMenuItem(item, index))}
        </div>
        {formikTouched && formikErrors && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block text-danger'>
              <span role='alert'>{formikErrors}</span>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export {PackageType}
